import type { TFunction } from 'i18next';
import type { Metric, MetricStatus } from 'types/engagement.types';

/**
 * Rendering numbers that can be missing (contract: "Fehlschlag ≠ Leere").
 *
 * A Metric that is not `ok` is NEVER shown as 0 — an unconnected ad account rendered as "0 €" reads
 * as a campaign that spent nothing. It renders its status as text instead ("noch nicht angebunden"),
 * with the server's reason as tooltip/subline.
 */

export const isMetric = (v: unknown): v is Metric<unknown> =>
  !!v && typeof v === 'object' && 'status' in (v as Record<string, unknown>) && 'value' in (v as Record<string, unknown>);

export type Formatter = (n: number) => string;

const locale = (lng?: string) => (lng && lng.startsWith('en') ? 'en-GB' : 'de-DE');

export const fmtInt = (lng?: string): Formatter => (n) => new Intl.NumberFormat(locale(lng), { maximumFractionDigits: 0 }).format(n);

export const fmtDec = (lng?: string, digits = 1): Formatter => (n) =>
  new Intl.NumberFormat(locale(lng), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);

/** Ratio 0..1 → "31 %". Values > 1 are assumed to already be percent. */
export const fmtPct = (lng?: string, digits = 0): Formatter => (n) => {
  const v = n > 1 ? n / 100 : n;
  return new Intl.NumberFormat(locale(lng), { style: 'percent', maximumFractionDigits: digits, minimumFractionDigits: digits }).format(v);
};

export const fmtEuroCents = (lng?: string): Formatter => (cents) =>
  new Intl.NumberFormat(locale(lng), { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(cents / 100);

export const fmtDate = (iso: string | null | undefined, lng?: string, withTime = false): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale(lng), withTime ? { dateStyle: 'short', timeStyle: 'short' } : { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
};

export const metricStatusLabel = (status: MetricStatus, t: TFunction): string => {
  switch (status) {
    case 'not_connected':
      return t('engMetricNotConnected');
    case 'failed':
      return t('engMetricFailed');
    case 'pending':
      return t('engMetricPending');
    default:
      return t('engMetricNoData');
  }
};

export interface DescribedMetric {
  text: string;
  /** true when `text` is a value, false when it is a status/reason. */
  hasValue: boolean;
  status: MetricStatus;
  reason?: string;
}

/** Plain number or Metric → display text. A missing value is described, never zeroed. */
export const describeMetric = (v: number | Metric | null | undefined, format: Formatter, t: TFunction): DescribedMetric => {
  if (typeof v === 'number' && Number.isFinite(v)) return { text: format(v), hasValue: true, status: 'ok' };
  if (isMetric(v)) {
    if (v.status === 'ok' && typeof v.value === 'number' && Number.isFinite(v.value)) {
      return { text: format(v.value), hasValue: true, status: 'ok', reason: v.reason };
    }
    return { text: metricStatusLabel(v.status === 'ok' ? 'pending' : v.status, t), hasValue: false, status: v.status, reason: v.reason };
  }
  return { text: t('engMetricNoData'), hasValue: false, status: 'pending' };
};
